/**
 * RoyaleSettings
 *
 * Host-options body for Puzzle Royale, mounted inside a SlideModal from the
 * Create flow. Controlled component — receives the current RoyaleSettings +
 * onChange; the lobby decides when to commit (the host taps "Create lobby",
 * which is disabled until at least one topic is picked).
 *
 * Fields (PRD):
 *   - Topic   : multi-select (CCSelect), REQUIRED — no default, can't be empty.
 *   - Time    : slider over TIME_CHIPS, default 90s.
 *   - Punish  : yes/no toggle (default no); when yes, threshold 5/10 (default 5).
 *   - Difficulty: easy / medium / hard (default easy) — maps to player-move-count.
 *
 * All option sets + defaults come from royaleConstants so they stay
 * configurable and in sync with the server.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCSelect from '@/Atoms/CCSelect';
import {FontSizes, vs} from '@/Utils/Dimensions';

import {
  DEMO_ASSESSMENT_LABELS,
  DEMO_ASSESSMENT_OPTIONS,
  DEMO_ASSESSMENT_RANGE_LABELS,
  DIFFICULTY_MOVE_LABELS,
  DIFFICULTY_OPTIONS,
  MAX_TOPICS,
  PUNISH_THRESHOLDS,
  RATING_BAND_LABELS,
  RATING_BAND_OPTIONS,
  RATING_BAND_RANGE_LABELS,
  TIME_CHIPS,
  type DemoAssessment,
  type Difficulty,
  type MoveDifficulty,
  type RatingBand,
} from '@/Screens/FunChallenges/royaleConstants';
import {TOPIC_SELECT_ITEMS} from '@/Screens/FunChallenges/royaleTopics';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';

interface Props {
  value: RoyaleSettingsValue;
  onChange: (next: RoyaleSettingsValue) => void;
  /**
   * CLASSROOM ONLY. When true, the Difficulty picker also offers the rating
   * bands F1–F4 (in addition to easy/medium/hard) and shows the backfill
   * disclaimer. Standalone leaves this false so its behaviour is unchanged.
   */
  allowRatingBands?: boolean;
  /**
   * DEMO-CALL CLASSES ONLY. When true, the Difficulty section is REPLACED by
   * the curated Demo assessment picker (Demo B / F1 / F2) — the coach picks a
   * fixed puzzle set instead of a difficulty/rating band. Mutually the sole
   * difficulty control in that class (the normal chips + F-levels are hidden).
   * Only the classroom coach-create UI passes this, and only when the class
   * batch_type is 'Demo Call'.
   */
  allowDemoAssessments?: boolean;
}

export default function RoyaleSettings({
  value,
  onChange,
  allowRatingBands = false,
  allowDemoAssessments = false,
}: Props) {
  const isRatingBand = RATING_BAND_OPTIONS.includes(value.difficulty as RatingBand);
  const setAssessment = (assessment: DemoAssessment) =>
    onChange({...value, assessment});
  const setTopics = (topics: string[]) => {
    // Topics are optional; cap the selection at MAX_TOPICS. CCSelect may hand
    // back more than the cap, so trim (keep the first MAX_TOPICS picked).
    const capped = topics.slice(0, MAX_TOPICS);
    onChange({...value, topics: capped});
  };
  const setTimer = (timerSec: number) => onChange({...value, timerSec});
  const setDifficulty = (difficulty: Difficulty) =>
    onChange({...value, difficulty});
  const setPunish = (punish: boolean) => onChange({...value, punish});
  const setThreshold = (punishThreshold: number) =>
    onChange({...value, punishThreshold});

  return (
    <Box style={styles.root}>
      {/* Topic — optional multi-select, capped at MAX_TOPICS.
          The CCSelect dropdown is an absolutely-positioned overlay, so this
          Section is lifted with zIndex/elevation (mirrors the 1v1 lobby's
          opponent-picker fix) — otherwise the open menu is painted UNDER the
          Time / Difficulty sections that follow it.
          HIDDEN for Demo-Call assessments: a demo is a FIXED curated set, so a
          topic filter doesn't apply (the server ignores topics when an
          assessment is set). */}
      {!allowDemoAssessments && (
        <View style={styles.topicSection}>
          <Section
            title="Topics"
            hint={
              value.topics.length === 0
                ? `Optional · any theme · up to ${MAX_TOPICS}`
                : `${value.topics.length} / ${MAX_TOPICS} selected`
            }>
            {/* Use the searchable MODAL multiselect branch (not
                multiSelectDropdown) — that branch renders the "Search..." input +
                Clear/Apply footer, which the inline dropdown branch lacks. The
                coach asked for type-to-search over the ~60 topics. */}
            <CCSelect
              items={TOPIC_SELECT_ITEMS}
              selectedValues={value.topics}
              onValueChange={setTopics}
              multiSelect
              isSearchable
              limit={MAX_TOPICS}
              modalLabel="Select topics"
              placeholder="Select topics (optional)"
              headerComponent={<></>}
            />
          </Section>
        </View>
      )}

      {/* Time — discrete segmented chips over TIME_CHIPS.
          (Was @react-native-community/slider, but its .web component crashes
          with "Cannot read properties of undefined (reading 'toFixed')" on
          mount — and the options are discrete anyway, so a segmented control is
          both crash-free and a cleaner fit. Also fully fixes the old thumb /
          tick-label misalignment.) */}
      <Section title="Time" hint={`${value.timerSec}s`}>
        <View style={styles.timeRow}>
          {TIME_CHIPS.map(t => {
            const selected = value.timerSec === t;
            return (
              <Pressable
                key={t}
                onPress={() => setTimer(t)}
                style={[styles.timeChip, selected && styles.timeChipSelected]}>
                <CCText
                  style={[
                    styles.timeChipText,
                    selected && styles.timeChipTextSelected,
                  ]}>
                  {t}s
                </CCText>
              </Pressable>
            );
          })}
        </View>
      </Section>

      {/* Demo-Call classes: the Difficulty section is REPLACED by the curated
          Demo assessment picker (Demo B / F1 / F2). The coach picks a fixed
          puzzle set; difficulty/topics no longer apply. */}
      {allowDemoAssessments ? (
        <Section title="Assessment">
          <View style={styles.chipRow}>
            {DEMO_ASSESSMENT_OPTIONS.map((a: DemoAssessment) => {
              const selected = value.assessment === a;
              return (
                <Pressable
                  key={a}
                  onPress={() => setAssessment(a)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {DEMO_ASSESSMENT_LABELS[a]}
                  </CCText>
                  <CCText
                    style={[
                      styles.chipSub,
                      selected && styles.chipSubSelected,
                    ]}>
                    {DEMO_ASSESSMENT_RANGE_LABELS[a]}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
          <CCText style={styles.difficultyNote}>
            Demo sets are fixed, hinted puzzles. The student plays; you watch
            their board live as the spectator.
          </CCText>
        </Section>
      ) : (
      /* Difficulty — single-select. Move-count bands always; classroom adds
          the F-level rating bands (F1–F4) to the SAME group (still pick one). */
      <Section title="Difficulty">
        <View style={styles.chipRow}>
          {DIFFICULTY_OPTIONS.map((d: MoveDifficulty) => {
            const selected = value.difficulty === d;
            return (
              <Pressable
                key={d}
                onPress={() => setDifficulty(d)}
                style={[styles.chip, selected && styles.chipSelected]}>
                <CCText
                  style={[
                    styles.chipText,
                    selected && styles.chipTextSelected,
                  ]}>
                  {d[0].toUpperCase() + d.slice(1)}
                </CCText>
                <CCText
                  style={[styles.chipSub, selected && styles.chipSubSelected]}>
                  {DIFFICULTY_MOVE_LABELS[d]}
                </CCText>
              </Pressable>
            );
          })}
          {allowRatingBands &&
            RATING_BAND_OPTIONS.map((b: RatingBand) => {
              const selected = value.difficulty === b;
              return (
                <Pressable
                  key={b}
                  onPress={() => setDifficulty(b)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {RATING_BAND_LABELS[b]}
                  </CCText>
                  <CCText
                    style={[
                      styles.chipSub,
                      selected && styles.chipSubSelected,
                    ]}>
                    {RATING_BAND_RANGE_LABELS[b]}
                  </CCText>
                </Pressable>
              );
            })}
        </View>
        {allowRatingBands && (
          <CCText style={styles.difficultyNote}>
            {isRatingBand
              ? 'F-levels pick by rating (≤4 moves). Low on your topics? ' +
                'Other topics of this level fill in.'
              : 'F1–F4 pick by rating range (≤4 moves), not move count.'}
          </CCText>
        )}
      </Section>
      )}

      {/* Punish mistakes */}
      <Section title="Punish mistakes">
        <View style={styles.chipRow}>
          {[
            {label: 'No', val: false},
            {label: 'Yes', val: true},
          ].map(opt => {
            const selected = value.punish === opt.val;
            return (
              <Pressable
                key={opt.label}
                onPress={() => setPunish(opt.val)}
                style={[styles.chip, selected && styles.chipSelected]}>
                <CCText
                  style={[
                    styles.chipText,
                    selected && styles.chipTextSelected,
                  ]}>
                  {opt.label}
                </CCText>
              </Pressable>
            );
          })}
        </View>
      </Section>

      {/* Punish threshold — only when punish is on */}
      {value.punish && (
        <Section title="Out after">
          <View style={styles.chipRow}>
            {PUNISH_THRESHOLDS.map(n => {
              const selected = (value.punishThreshold ?? 5) === n;
              return (
                <Pressable
                  key={n}
                  onPress={() => setThreshold(n)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {n} wrong
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </Section>
      )}
    </Box>
  );
}

function Section({
  title,
  hint,
  hintError,
  children,
}: {
  title: string;
  hint?: string;
  hintError?: boolean;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <CCText style={styles.sectionTitle}>{title}</CCText>
        {hint ? (
          <CCText
            style={[styles.sectionHint, hintError && styles.sectionHintError]}>
            {hint}
          </CCText>
        ) : null}
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {paddingVertical: vs(8)},
  section: {marginBottom: vs(20)},
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: vs(8),
  },
  sectionTitle: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '700',
    color: TEXT_DARK,
  },
  sectionHint: {
    fontSize: FontSizes[12] || 12,
    color: TEAL_DARK,
    fontWeight: '600',
  },
  sectionHintError: {
    color: '#CD2C39',
  },
  chipRow: {flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: vs(6)},
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: CCColors.White,
    alignItems: 'center',
  },
  chipSelected: {
    backgroundColor: BROWN,
    borderColor: BROWN,
  },
  chipText: {
    fontSize: FontSizes[14] || 14,
    color: CCColors.GreyDarkest ?? '#444',
    fontWeight: '600',
  },
  chipTextSelected: {
    color: CCColors.White,
    fontWeight: '700',
  },
  chipSub: {
    fontSize: FontSizes[10] || 10,
    color: '#8A7E5F',
    marginTop: 2,
  },
  difficultyNote: {
    fontSize: FontSizes[11] || 11,
    color: TEAL_DARK,
    marginTop: vs(8),
    lineHeight: 16,
    // Reserve two lines so the modal height doesn't jump when the note changes
    // length (or appears) as the coach switches between move/F-level chips.
    minHeight: 32,
  },
  chipSubSelected: {
    color: 'rgba(255,255,255,0.75)',
  },
  // Lifts the Topics section + its absolute dropdown overlay above the Time /
  // Difficulty sections that follow (the dropdown is position:absolute).
  topicSection: {
    zIndex: 100,
    elevation: 100,
    position: 'relative',
  },
  timeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: vs(2),
  },
  timeChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: CCColors.White,
    alignItems: 'center',
  },
  timeChipSelected: {
    backgroundColor: TEAL,
    borderColor: TEAL,
  },
  timeChipText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '600',
    color: CCColors.GreyDarkest ?? '#444',
  },
  timeChipTextSelected: {
    color: CCColors.White,
    fontWeight: '700',
  },
});
